import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowPathIcon, PaperAirplaneIcon, FaceSmileIcon } from '@heroicons/react/24/outline';
import { chatbotService } from '../services/chatbotService';
import { sendMessageApi, sendMessageStreamApi } from '../services/chatService';
import { renderChatMarkdown } from '../utility/renderMarkdownText';
import { ShowImage } from '../utils/ShowImage';
import { SymptomCheckerAssessmentMessageBubble, VideoModal } from '../components/chatbot/tabs/ConversationsTab';
import { LeadCaptureFormBubble } from '../components/chat/LeadCaptureFormBubble';
import { useBranding } from '../context/BrandingContext';
import { PoweredBy } from '../components/ui/BrandFooter';
import { cssColor, resolveColor } from '../theme/engine';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import { Overlay } from '../components/ui/Overlay';
import { getOrCreateAnonymousId, readStoredConversationId, storeConversationId } from '../utils/visitorIdentity';

/**
 * Colours used when the chatbot's own theme leaves one unset. They follow the
 * client's (or product's) theme; the chatbot's theme config always wins.
 */
const FALLBACK = {
  brandGradient: `linear-gradient(90deg, ${cssColor('abcad9')} 0%, ${cssColor('5484c5')} 100%)`,
  pageGradient: `linear-gradient(180deg, ${cssColor('f5f7ff')} 0%, ${cssColor('e3ebff')} 42%, ${cssColor('eef6ff')} 100%)`,
  sendButton: cssColor('3b82f6'),
  border: cssColor('e5e7eb'),
  mutedText: cssColor('6b7280'),
};

/** Chatbot ids whose widget never shows the "Powered by ..." footer — a one-off, per-chatbot
 *  opt-out until there's a real tenant-level setting for this. Currently: Pacific One Health
 *  (chatbot "Assistant", platform.alphapods.app). */
const HIDE_POWERED_BY_CHATBOT_IDS = new Set<string>([
  '753700e2-9c6c-44cb-93a4-ab619e51c37f',
]);

/** The launcher colour a chatbot's own theme asks for, if any. */
function themeConfigLauncherColor(chatbotData: any): string | null {
  const config = chatbotData?.themeConfig?.settings || chatbotData?.themeSettings?.settings || chatbotData?.settings || {};
  const color = config.sendButtonColor || config.sendBtn;
  return typeof color === 'string' && color ? color : null;
}

/** Sentinel for "the chatbot set no page background". */
const DEFAULT_BG = '#f0f6ff';

/**
 * An entry-choice button click sends plain natural-language text — not a special payload — so
 * the backend treats it exactly like anything the visitor could have typed themselves, through
 * the same intent classification every other message goes through. No backend changes needed.
 */
function entryChoiceMessageFor(intent: string): string {
  if (intent === 'booking') return 'I want to book an appointment';
  if (intent === 'faq') return 'I have a question';
  return intent;
}



export type TabId = 'general' | 'themes' | 'knowledge' | 'playground' | 'connect' | 'conversations';

export type IngestFlow = 'symptom-checker' | 'knowledge-base' | 'knowledge-base-qa';


export interface TabDef {
  id: TabId;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<{ chatbot: any }>;
}


export type ThemeState = {
  userMsgBg: string;
  userMsgFont: string;
  botMsgBg: string;
  botMsgFont: string;
  sendBtn: string;
  bg: string;
  inputTextBg: string;
  inputTextFont: string;
  inputBorder: string;
  fontFamily: string;
  welcome: string;
  placeholder: string;
  headerBg: any;
  headerTextFont: any
};


export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: any;
  metadata?: any
  type?: "normal" | "assessment" | "lead-capture-form";
};


export type ConversationSummary = {
  id: string;
  tenantId?: string | null;
  chatbotId?: string | null;
  status?: string | null;
  title?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  messageCount?: number;
  createdAt?: string;
  updatedAt?: string;
};


export type ConversationMessage = {
  id: string;
  conversationId: string;
  senderType: 'user' | 'agent' | string;
  message: string;
  messageType?: string;
  createdAt: string;
};


export type KBType = 'pdf' | 'text' | 'image' | 'url' | 'sitemap' | 'video';
export type KBStatus = 'processed' | 'processing' | 'error';


export interface KBItem {
  id: string;
  name: string;
  type: KBType;
  size?: string;
  lastUpdated: string;
  status: KBStatus;
}


// IMAGE GALLERY TYPES AND COMPONENTS
interface ImageGroup {
  description: string;
  groupId: string;
  images: Array<{
    index: number;
    imageUrl: string;
    orderInGroup: number;
  }>;
}

type RightSectionData = {
  sectionHeading: string
  sectionCountStats: {
    currentValue: number
    totalValue: number
  }
  data: Array<{
    id: number | string | null
    title: string
    descriptionLines: string[]
    image: string | null
  }>
}

const ImageGallery: React.FC<{ images: ImageGroup[] }> = ({ images }) => {
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {images.map((group) => (
        <div key={group.groupId} className="space-y-3">
          {/* Group Heading */}
          <div className="px-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {group.description}
            </h3>
          </div>

          {/* Images in Group */}
          <div className="space-y-3">
            {group.images.map((img) => (
              <div
                key={`${group.groupId}-${img.index}`}
                className="w-full rounded-2xl overflow-hidden hover:shadow-md transition-shadow px-4 py-4 border-violet-400 bg-violet-50/60 shadow-[0_0_0_1px_rgb(var(--c-8b5cf6,139_92_246)/0.35)]"
              >
                <img
                  src={img.imageUrl}
                  alt={group.description}
                  className="w-full h-auto object-cover max-h-64"
                  onError={(e) => {
                    e.currentTarget.src = '/default-image.png';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

function useQueryParam(name: string) {
  return new URLSearchParams(window.location.search).get(name);
}

export const AssessmentCarousel: React.FC<{
  content: any[];
}> = ({ content }) => {
  const [activeVideo, setActiveVideo] = React.useState<string | null>(null);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // 🔥 Prevent vertical scroll when swiping horizontally
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!content?.length) return null;

  return (
    <div className="mt-4">
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto overscroll-x-contain scroll-smooth snap-x snap-mandatory"
      >
        {content.map((item, index) => (
          <div
            key={index}
            className="w-[90%] flex-shrink-0 snap-start rounded-2xl border border-violet-200 bg-gradient-to-br from-white to-violet-50 p-5 shadow-sm"
          >
            {/* Text Content */}
            <div className="prose prose-sm max-w-none">
              {renderChatMarkdown(item.formattedText)}
            </div>

            {/* Divider */}
            <div className="my-4 h-px bg-violet-100" />

            {/* Video Section */}
            {item.metadata?.media?.length ? (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">
                  Recommended Videos
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {item.metadata?.media.map((video: any, vidIndex: number) => (
                    <div
                      key={vidIndex}
                      onClick={() => setActiveVideo(video.url)}
                      className="group relative rounded-xl overflow-hidden bg-black aspect-video cursor-pointer"
                    >
                      <video
                        src={video.url}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        preload="metadata"
                      />

                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />

                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                          <svg
                            className="w-5 h-5 text-violet-600 ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M6 4l10 6-10 6V4z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : ""}
          </div>
        ))}
      </div>
      {activeVideo && (
        <VideoModal
          videoSrc={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </div>
  );
};

const PublicChatWidget: React.FC = () => {
  const { branding, showTenant } = useBranding();
  const { chatbotId } = useParams<{ chatbotId: string; clientId?: string }>();
  const [userId] = useState<string>(() => `guest-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  // Persistent visitor id: survives reloads and return visits (localStorage, this widget's own
  // origin) so the backend can recognise the same person and remember what they already said.
  const [anonymousId] = useState<string>(() => getOrCreateAnonymousId());

  const viewType: string | undefined = useQueryParam("view-type") || undefined
  const externalUserId: string | undefined = useQueryParam("external-user-id") || undefined
  const externalUserProfileId: string | undefined = useQueryParam("external-user-profile-id") || undefined
  const selectedConversationId: string | undefined = useQueryParam("conversation-id") || undefined

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const slug = useQueryParam("slug") || undefined;


  // Title, favicon and colours come from branding, switched to the chatbot's
  // client once the chatbot has loaded (visitors are not signed in).
  const [launcherColor, setLauncherColor] = useState<string | null>(null);

  // State
  const [chatbot, setChatbot] = useState<any>(null);
  const [theme, setTheme] = useState<any>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialMessages: ChatMessage[] = [];
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [imageGallery, setImageGallery] = useState<ImageGroup[]>([]);
  const [rightSectionHeading, setRightSectionHeading] = useState<string>("Key Highlights")
  const [rightSectionData, setRightSectionData] = useState<RightSectionData[]>([])
  const [activeRightSectionItemId, setActiveRightSectionItemId] = useState<number | string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [userTenantProfile, setUserTenantProfile] = useState<any>(null);
  // Guards loadChatbot's welcome-message reset against clobbering real conversation messages
  // loaded by the independent loadConversation/loadLatestConversation effect — see its usage below.
  const hasLoadedRealMessagesRef = useRef(false);

  useEffect(() => {
    const tenantId = chatbot?.tenantId || chatbot?.tenant?.id || null;
    if (tenantId) showTenant(tenantId);
    return () => showTenant(null);
  }, [chatbot?.tenantId, chatbot?.tenant?.id, showTenant]);

  // Tell the embed script on the host website what colour its launcher button
  // should be: the chatbot's own theme first, then the client's or product's
  // brand. The host page cannot read this app's theme, so real colours are sent.
  useEffect(() => {
    if (!chatbot || window.parent === window) return;
    const background =
      launcherColor ||
      `linear-gradient(135deg, ${resolveColor('#667eea', branding.colors)} 0%, ${resolveColor('#764ba2', branding.colors)} 100%)`;
    window.parent.postMessage({ type: 'chat-widget-theme', launcherBackground: background }, '*');
  }, [chatbot, launcherColor, branding.colors]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!isSending && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isSending]);

  // Load chatbot configuration and theme
  const loadChatbot = useCallback(async () => {
    if (!chatbotId) {
      setError('Chatbot ID is required');
      setIsLoading(false);
      return;
    }

    const loadChatbot = async () => {
      try {
        setIsLoading(true);
        const resp = await chatbotService.getChatbotById(chatbotId);
        const chatbotData = (resp as any)?.data ?? resp;
        setChatbot(chatbotData);
        setLauncherColor(themeConfigLauncherColor(chatbotData));

        // Extract theme settings
        const themeConfig = chatbotData?.themeConfig?.settings ||
          chatbotData?.themeSettings?.settings ||
          chatbotData?.settings || {};
        setTheme({
          userMsgBg: themeConfig.userMessageBgColor || themeConfig.userMessageBg || FALLBACK.brandGradient,
          userMsgFont: themeConfig.userMessageFontColor || themeConfig.userMessageFont || '#ffffff',
          botMsgBg: themeConfig.botMessageBgColor || themeConfig.botMsgBg || '#ffffff',
          headerBgColor: themeConfig.headerBgColor || themeConfig.headerBgColor || '#ffffff',
          botMsgFont: themeConfig.botMessageFontColor || themeConfig.botMsgFont || '#000000',
          sendBtn: themeConfig.sendButtonColor || themeConfig.sendBtn || FALLBACK.sendButton,
          bg: themeConfig.bgColor || themeConfig.bg || DEFAULT_BG,
          inputTextBg: themeConfig.inputTextBgColor || themeConfig.inputTextBg || '#ffffff',
          inputTextFont: themeConfig.inputTextFontColor || themeConfig.inputTextFont || '#000000',
          inputBorder: themeConfig.inputFieldBorderColor || themeConfig.inputBorder || FALLBACK.border,
          fontFamily: themeConfig.fontFamily || 'Outfit, sans-serif',
          welcome: themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?',
          placeholder: themeConfig.placeholderText || themeConfig.placeholder || 'Type your message...',
          headerTextFont: themeConfig.headerTextFont || "#fff"
        });

        // Add welcome message — unless the independent conversation-history load (triggered by
        // the same mount, not awaited here) already finished first and set real messages. That
        // effect and this one race on the same `messages` state with no ordering guarantee;
        // without this guard, whichever resolves last wins, so a slow-to-resolve chatbot-config
        // fetch could silently wipe out an already-loaded conversation back to "new conversation".
        if (!hasLoadedRealMessagesRef.current) {
          const welcomeContent = themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?';
          const welcomeMsg: ChatMessage = { id: 'welcome', role: 'assistant', content: welcomeContent };

          // Entry-choice buttons render directly under the welcome bubble, before the visitor
          // sends anything — a separate local message right after it, same pattern the backend
          // uses for a resumed/real conversation's own entry-choice prompt (metadata.type ===
          // 'entry-choice'), just shown instantly here instead of waiting on a round trip.
          const entryChoices = chatbotData?.config?.entryChoices;
          if (entryChoices?.enabled && Array.isArray(entryChoices.buttons) && entryChoices.buttons.length > 0) {
            setMessages([
              welcomeMsg,
              {
                id: 'welcome-entry-choice',
                role: 'assistant',
                content: '',
                metadata: { type: 'entry-choice', choices: entryChoices.buttons },
              },
            ]);
          } else {
            setMessages([welcomeMsg]);
          }
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error('Failed to load chatbot:', err);
        setError('Failed to load chatbot configuration');
        setIsLoading(false);
      }
    };

    loadChatbot();
  }, [chatbotId]);

  useEffect(() => {
    if (!chatbotId) return;
    loadChatbot();
  }, [chatbotId]);

  useEffect(() => {
    // Sync (find-or-create) the TenantUserProfile whenever we know who the visitor is,
    // regardless of useClientProfile — that flag only controls whether profile data gets
    // fed into the AI prompt, not whether the conversation gets linked to the user. Without
    // this, conversations for chatbots/agents that don't set useClientProfile never receive
    // a userTenantProfileId, so they can never be found again via GET /conversation-history's
    // externalUserId/externalUserProfileId lookup.
    if (chatbot && chatbotId && externalUserId && slug) {
      const syncProfile = async () => {
        try {
          const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
          const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
          const tenantId = chatbotTenantId || lsTenantId;
          // Deliberately doesn't touch isLoading — that flag gates the full-page loading
          // screen (replaces the whole widget, messages included; see the `if (isLoading)`
          // return below), and this sync now runs for every chatbot, not just useClientProfile
          // ones. Toggling it here re-triggered that full-page takeover, hiding messages that
          // loadConversation/loadLatestConversation had already loaded correctly.
          const userProfile = await chatbotService.syncTenantUserProfile({
            chatbotId,
            tenantId,
            externalUserId,
            externalUserProfileId,
            slug
          });
          setUserTenantProfile(userProfile);
        } catch (err) {
          console.error('Failed to sync tenant user profile:', err);
        }
      }

      syncProfile()
    }
  }, [chatbotId, chatbot, externalUserId, externalUserProfileId, slug]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const resetChat = () => {
    const welcomeMsg = theme?.welcome || 'Hi! How can I help you today?';
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: welcomeMsg
    }]);
    setConversationId(undefined);
    setInput('');
    setImageGallery([]);
  };

  /**
   * `silent`: for resuming the conversation this browser was in (per localStorage), where a stale id
   * (e.g. the conversation was since deleted) should just fall back to a fresh conversation
   * instead of taking over the widget with an error screen — the tab was never meant to get stuck
   * over a resume-only id. An explicit `conversation-id` link (the non-silent, default case) still
   * surfaces a real failure as before — that's a deliberate ask the host page made, not a resume.
   */
  const loadConversation = async (conversationId: string, opts?: { silent?: boolean }) => {
    setIsLoading(true);
    if (!opts?.silent) setError(null);
    try {
      const conv: any = await chatbotService.singleConversationHistoryById(conversationId);
      // ensure messages are sorted by createdAt ascending
      const newMessages: any = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => {
          const ta = new Date(a.createdAt).getTime();
          const tb = new Date(b.createdAt).getTime();
          return ta - tb;
        })
        .map((message: any) => ({
          ...message,
          metadata: message.metadata && typeof message.metadata === "string" ? JSON.parse(message.metadata) : message.metadata
        }));

      hasLoadedRealMessagesRef.current = true;
      setConversationId(conv.id)
      setMessages(newMessages);
    } catch (err: any) {
      console.error('Failed to load conversation', err);
      if (opts?.silent) {
        console.warn('Stored conversation is no longer available — starting a new one.');
        storeConversationId(chatbotId, undefined);
      } else {
        setError('Failed to load conversation messages.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadLatestConversation = async () => {
    try {
      if (!chatbotId) {
        return null
      }

      const conv: any = await chatbotService.listConversationHistory(chatbotId, {
        externalUserId,
        externalUserProfileId,
        limit: 1
      });

      if (!conv) return;

      const newMessages = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => {
          const ta = new Date(a.createdAt).getTime();
          const tb = new Date(b.createdAt).getTime();
          return ta - tb;
        })
        .map((message: any) => ({
          ...message,
          metadata: message.metadata && typeof message.metadata === "string" ? JSON.parse(message.metadata) : message.metadata
        }));

      hasLoadedRealMessagesRef.current = true;
      setConversationId(conv.id)
      setMessages(newMessages);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (selectedConversationId) {
      loadConversation(selectedConversationId!)
    } else if (externalUserId || externalUserProfileId) {
      loadLatestConversation();
    } else {
      // A reload mid-chat used to start a brand-new conversation (and lose everything the person
      // had already said). Resume the one this tab was in instead.
      const stored = readStoredConversationId(chatbotId);
      if (stored) loadConversation(stored, { silent: true });
    }
  }, [selectedConversationId, externalUserId, externalUserProfileId])

  // Mirror the active conversation into localStorage (cleared on "new conversation") so the
  // resume above has something to resume — shared across every tab/window of this browser.
  useEffect(() => {
    storeConversationId(chatbotId, conversationId);
  }, [chatbotId, conversationId]);
  // const sendMessage = async () => {
  //   const trimmed = input.trim();
  //   if (!trimmed || isSending) return;

  //   const userMsg: ChatMessage = {
  //     id: `u-${Date.now()}`,
  //     role: 'user',
  //     content: trimmed,
  //   };
  //   setMessages((prev) => [...prev, userMsg]);
  //   setInput('');
  //   setIsSending(true);

  //   // Reset textarea height
  //   if (textareaRef.current) {
  //     textareaRef.current.style.height = 'auto';
  //   }

  //   try {
  //     const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
  //     const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
  //     const tenantId = chatbotTenantId || lsTenantId;

  //     if (!tenantId) {
  //       throw new Error('Tenant ID is required but not found');
  //     }

  //     const payload: any = {
  //       chatbotId: chatbotId,
  //       tenantId: tenantId,
  //       userId: userId,
  //       message: trimmed,
  //       viewType,
  //     };

  //     if (conversationId) {
  //       payload.conversationId = conversationId;
  //     }

  //     console.log('Sending message:', { chatbotId, userId, tenantId, hasConversationId: !!conversationId });

  //     let response;
  //     try {
  //       response = await sendMessageApi(payload, false);
  //     } catch (err: any) {
  //       if (err?.status === 401 || err?.status === 403) {
  //         console.log('Retrying with authentication...');
  //         response = await sendMessageApi(payload, true);
  //       } else {
  //         throw err;
  //       }
  //     }

  //     console.log('🔍 API Response Debug:', response);

  //     const responseData = (response as any)?.data || response;

  //     const newConversationId = responseData?.conversationId || responseData?.conversation_id;
  //     if (!conversationId && newConversationId) {
  //       setConversationId(newConversationId);
  //     }

  //     const reply = responseData?.reply ||
  //       responseData?.message ||
  //       responseData?.response ||
  //       responseData?.text ||
  //       responseData?.content ||
  //       responseData?.answer ||
  //       (responseData?.data && (responseData.data.reply || responseData.data.message || responseData.data.response));

  //     const isErrorResponse = reply && (
  //       reply.toLowerCase().includes('unable to answer') ||
  //       reply.toLowerCase().includes('unable to') ||
  //       reply.toLowerCase().includes('i cannot') ||
  //       reply.toLowerCase().includes('i don\'t have') ||
  //       reply.toLowerCase().includes('no information')
  //     );

  //     if (isErrorResponse) {
  //       console.warn('⚠️ Backend returned error response:', reply);
  //       console.warn('⚠️ Full response data:', responseData);
  //     }

  //     const assistantMsg: ChatMessage = {
  //       id: `a-${Date.now() + 1}`,
  //       role: 'assistant',
  //       content: reply || 'Sorry, I am unable to answer this question with the available information.',
  //     };
  //     setMessages((prev) => [...prev, assistantMsg]);

  //     switch ((responseData?.metadata?.flow || null) as IngestFlow | null) {
  //       case "symptom-checker":
  //         setRightSectionHeading("Key Highlights of Assessment")
  //         setRightSectionData([])
  //         setActiveRightSectionItemId(null)

  //         if (responseData.metadata.matchedConditions?.length) {
  //           // Sort by relevanceScore in descending order (highest first)
  //           const matchingConditions = responseData.metadata.matchedConditions
  //             .sort((a: any, b: any) => b.relevanceScore - a.relevanceScore);

  //           const rightSectionData: RightSectionData[] = [{
  //             sectionHeading: "Matching Conditions",
  //             sectionCountStats: {
  //               currentValue: matchingConditions.length,
  //               totalValue: 0
  //             },
  //             data: matchingConditions.map((condition: any) => ({
  //               id: condition.docId,
  //               title: condition.conditionName,
  //               descriptionLines: [
  //                 `Probability: ${(condition.relevanceScore * 100).toFixed(0)}%`,
  //                 `Other symptoms: ${condition.symptomsText}`
  //               ],
  //               image: null
  //             }))
  //           }]
  //           setRightSectionData(rightSectionData)
  //         }

  //         break

  //       case "knowledge-base":
  //         setRightSectionHeading("Key Highlights")
  //         setActiveRightSectionItemId(null)

  //         // Extract relevant images from metadata
  //         let relevantImages = responseData?.metadata?.relevantImages || []
  //         if (relevantImages) {
  //           try {
  //             relevantImages = typeof relevantImages === 'string'
  //               ? JSON.parse(relevantImages)
  //               : relevantImages;

  //             if (Array.isArray(relevantImages) && relevantImages.length > 0) {
  //               const newRightSectionData: RightSectionData[] = relevantImages.map((el) => ({
  //                 sectionHeading: el.sectionHeading,
  //                 sectionCountStats: {
  //                   currentValue: el.images.length,
  //                   totalValue: 0
  //                 },
  //                 data: el.images.map((image: any) => ({
  //                   id: image.imageIndex,
  //                   title: image.aboutImage,
  //                   descriptionLines: [],
  //                   image: image.imageUrl
  //                 }))
  //               }))
  //               setRightSectionData([...rightSectionData, ...newRightSectionData])
  //             }
  //           } catch (imgErr) {
  //             console.warn('Failed to parse images:', imgErr);
  //           }
  //         }

  //         break

  //       default:
  //         setRightSectionHeading("Key Highlights")
  //         setRightSectionData([])
  //         setActiveRightSectionItemId(null)
  //     }

  //   } catch (err: any) {
  //     console.error('Failed to send message:', err);

  //     let errorMessage = 'Sorry, I encountered an error. Please try again.';
  //     if (err?.message) {
  //       errorMessage = err.message;
  //     } else if (err?.details?.error) {
  //       errorMessage = err.details.error;
  //     } else if (err?.details?.message) {
  //       errorMessage = err.details.message;
  //     } else if (typeof err === 'string') {
  //       errorMessage = err;
  //     }

  //     const errMsg: ChatMessage = {
  //       id: `e-${Date.now() + 2}`,
  //       role: 'assistant',
  //       content: errorMessage,
  //     };
  //     setMessages((prev) => [...prev, errMsg]);
  //   } finally {
  //     setIsSending(false);
  //   }
  // };

  /**
   * `overrides` lets a non-typed-text sender reuse this exact same send/stream/render pipeline
   * instead of going through the textarea's `input` state: a synthetic `messageText`, plus extra
   * `additionalInputParams` merged in. Normal typed sends (the input box) call this with no
   * arguments, unchanged from before. Two current callers:
   *  - LeadCaptureFormBubble's Submit button passes `messageText`/`additionalInputParams` (e.g.
   *    `leadFormSubmission`) plus `afterFormSubmission: true`, which marks a form-submit send
   *    specifically: once ITS OWN response comes back, if that response is the bullet-point
   *    summary (not another form re-render, which happens on a bad/partial submission), a short
   *    fixed confirmation reply is auto-sent right after — the backend's own next turn is what
   *    actually finalizes the lead (see LeadCaptureNodeExecutor's `isComplete && !submitted`
   *    branch); this never fires for a plain typed message.
   *  - Entry-choice button clicks pass `messageText` (entryChoiceMessageFor) so the choice goes
   *    through the identical send/stream/persist path as typed text, as plain natural language
   *    the backend classifies the same way it would anything the visitor typed.
   * `hideUserBubble: true` skips adding a user-facing chat bubble for this turn (used by both
   * the lead-form submit, whose form bubble already shows what was entered, and entry-choice
   * clicks, whose button already showed the visitor's choice) — the backend still receives the
   * real text and the bot's reply still appears normally.
   */
  const sendMessage = async (overrides?: { messageText?: string; additionalInputParams?: Record<string, any>; hideUserBubble?: boolean; afterFormSubmission?: boolean }) => {
    const trimmed = (overrides?.messageText ?? input).trim();
    if (!trimmed || isSending) return;

    const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
    const chatbotTenantId = chatbot?.tenantId || chatbot?.tenant?.id;
    const tenantId = chatbotTenantId || lsTenantId;

    if (!tenantId) {
      throw new Error('Tenant ID is required but not found');
    }

    if (!overrides?.hideUserBubble) {
      const userMsg: ChatMessage = {
        id: `u-${Date.now()}`,
        role: 'user',
        content: trimmed,
      };
      setMessages(prev => [...prev, userMsg]);
    }
    if (overrides?.messageText === undefined) setInput('');
    setIsSending(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Add streaming message placeholder
    const streamingMsgId = `a-${Date.now()}`;
    const streamingMsg: ChatMessage = {
      id: streamingMsgId,
      role: 'assistant',
      content: '',
    };
    setMessages(prev => [...prev, streamingMsg]);

    const payload: any = {
      chatbotId,
      tenantId,
      userId,
      anonymousId,
      message: trimmed,
      viewType,
      additionalInputParams: {
        externalUserId,
        externalUserProfileId,
        ...(overrides?.additionalInputParams ?? {}),
      },
      userTenantProfileId: userTenantProfile?.id,
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (conversationId) {
      payload.conversationId = conversationId;
    }

    try {
      let response;
      try {
        response = await sendMessageStreamApi(payload, false);
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          console.log('Retrying with authentication...');
          response = await sendMessageStreamApi(payload, true);
        } else {
          throw err;
        }
      }

      console.log('[Frontend] Response received:', {
        status: response.status,
        headers: {
          contentType: response.headers.get('content-type'),
          cacheControl: response.headers.get('cache-control'),
          connection: response.headers.get('connection'),
        },
        hasBody: !!response.body,
      });

      if (!response.ok) {
        console.error('[Frontend] Response not ok');
        throw new Error('Network error');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        console.error('[Frontend] No reader available');
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let buffer = '';
      let finalCompleteChunk: any = null;
      let hasReceivedFirstChunk = false;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('[Frontend] Stream done');
          break;
        }

        if (value) {
          buffer += decoder.decode(value, { stream: true });
          console.log('[Frontend] Received chunk:', buffer.substring(0, 100));

          const parts = buffer.split('\n\n');
          buffer = parts[parts.length - 1];

          for (let i = 0; i < parts.length - 1; i++) {
            const line = parts[i].trim();
            if (!line.startsWith('data: ')) continue;

            try {
              const jsonStr = line.replace('data: ', '');
              const data: any = JSON.parse(jsonStr);

              console.log('[Frontend] Parsed data:', data.type);

              const isAssessment =
                data.metadata?.flow === "symptom-checker" &&
                data.metadata?.isAssessment === true;
              const isLeadCaptureForm = data.metadata?.type === "lead-capture-form";
              const messageType = isAssessment ? "assessment" : isLeadCaptureForm ? "lead-capture-form" : "normal";

              if (data.type === 'content') {
                // On VERY FIRST real content, clear the placeholder text
                if (!hasReceivedFirstChunk) {
                  hasReceivedFirstChunk = true;
                  accumulatedContent = '';
                }

                accumulatedContent += data.content;
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === streamingMsgId
                      ? { ...msg, content: accumulatedContent, metadata: data.metadata, type: messageType }
                      : msg
                  )
                );
              } else if (data.type === 'complete') {
                finalCompleteChunk = data;

                const finalContent = data.content || accumulatedContent;
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === streamingMsgId
                      ? { ...msg, content: data.content, metadata: data.metadata, type: messageType }
                      : msg
                  )
                );

                if (!conversationId && data.conversationId) {
                  setConversationId(data.conversationId);
                }

                // See sendMessage's `afterFormSubmission` doc comment: this response is either the
                // bullet-point summary (auto-confirm now) or another form render (a bad/partial
                // submission was re-opened — nothing to confirm yet, wait for a real resubmission).
                if (overrides?.afterFormSubmission && data.metadata?.type !== 'lead-capture-form') {
                  setTimeout(() => {
                    sendMessage({ messageText: 'The provided details are correct', hideUserBubble: false });
                  }, 300);
                }

                if (data.metadata?.relevantImages) {
                  setRightSectionData(data.metadata.relevantImages);
                }

                switch ((data?.metadata?.flow || null) as IngestFlow | null) {
                  case "symptom-checker":
                    setRightSectionHeading("Key Highlights of Assessment")
                    setRightSectionData([])
                    setActiveRightSectionItemId(null)

                    if (data.metadata.matchedConditions?.length) {
                      // Sort by relevanceScore in descending order (highest first)
                      const matchingConditions = data.metadata.matchedConditions
                        .sort((a: any, b: any) => b.relevanceScore - a.relevanceScore);

                      const rightSectionData: RightSectionData[] = [{
                        sectionHeading: "Matching Conditions",
                        sectionCountStats: {
                          currentValue: matchingConditions.length,
                          totalValue: 0
                        },
                        data: matchingConditions.map((condition: any) => ({
                          id: condition.docId,
                          title: condition.conditionName,
                          descriptionLines: [
                            `Probability: ${(condition.relevanceScore * 100).toFixed(0)}%`,
                            `Other symptoms: ${condition.symptomsText}`
                          ],
                          image: null
                        }))
                      }]
                      setRightSectionData(rightSectionData)
                    }

                    break

                  case "knowledge-base":
                    setRightSectionHeading("Key Highlights")
                    setActiveRightSectionItemId(null)

                    // Extract relevant images from metadata
                    let relevantImages = data?.metadata?.relevantImages || []
                    if (relevantImages) {
                      try {
                        relevantImages = typeof relevantImages === 'string'
                          ? JSON.parse(relevantImages)
                          : relevantImages;

                        if (Array.isArray(relevantImages) && relevantImages.length > 0) {
                          const newRightSectionData: RightSectionData[] = relevantImages.map((el) => ({
                            sectionHeading: el.sectionHeading,
                            sectionCountStats: {
                              currentValue: el.images.length,
                              totalValue: 0
                            },
                            data: el.images.map((image: any) => ({
                              id: image.imageIndex,
                              title: image.aboutImage,
                              descriptionLines: [],
                              image: image.imageUrl
                            }))
                          }))
                          setRightSectionData([...rightSectionData, ...newRightSectionData])
                        }
                      } catch (imgErr) {
                        console.warn('Failed to parse images:', imgErr);
                      }
                    }

                    break

                  default:
                    setRightSectionHeading("Key Highlights")
                    setRightSectionData([])
                    setActiveRightSectionItemId(null)
                }
              }
            } catch (parseErr) {
              console.warn('[Frontend] Parse error:', parseErr);
            }
          }
        }

        // Safety: if stream ended without a `complete` but we got some chunks
        if (!finalCompleteChunk && accumulatedContent) {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === streamingMsgId
                ? { ...msg, content: accumulatedContent }
                : msg
            )
          );
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === streamingMsgId
            ? {
              ...msg,
              content: 'Sorry, I encountered an error. Please try again.',
            }
            : msg
        )
      );
    } finally {
      setIsSending(false);
    }
  };

  const chatShell = (
    <div
      className="relative flex flex-col h-full"
      style={{
        background:
          theme?.bg && theme.bg !== DEFAULT_BG
            ? theme.bg
            : FALLBACK.pageGradient,
        fontFamily: theme?.fontFamily || 'Outfit, sans-serif',
      }}
    >
      {/* Chat Header */}
      <div
        className="h-14 flex items-center justify-between px-4 shadow-sm"
        style={{
          background:
            theme?.headerBgColor ||
            FALLBACK.brandGradient,
          color: theme?.headerTextFont || '#ffffff',
        }}
      >
        <div className="flex items-center gap-2 font-semibold text-base">
          {chatbot?.s3ObjectData && (
            <div style={{ borderRadius: "50%", backgroundColor: "#ffffff", padding: "3px" }}>
              <ShowImage
                s3Params={chatbot?.s3ObjectData}
                src={branding.logoLightUrl || undefined}
                alt="bot"
                className="h-8 w-8 rounded-full object-cover"
                loading="eager"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.src = '/default-bot.png';
                }}
              />
            </div>
          )}

          <span style={{ color: chatbot?.headerTextFont }}>{chatbot?.name || 'Chat Support'}</span>
        </div>

        <Button tone="custom"
          onClick={resetChat}
          className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
          aria-label="Reset chat"
          title="Reset chat"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </Button>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m: any, index: number) => {
          const isUser = m.role === 'user' || m.senderType === 'user';

          let content: any = ""
          try {
            content = JSON.parse(m.content ?? m.message)
          } catch (err) {
            content = m.content ?? m.message
          }

          const { isAssessment } = m.metadata || {};
          const isLeadCaptureForm = m.type === 'lead-capture-form' || m.metadata?.type === 'lead-capture-form';
          const isEntryChoice = m.metadata?.type === 'entry-choice' && Array.isArray(m.metadata?.choices);

          const messageContent: string = m.content ?? m.message

          if (isEntryChoice) {
            // Once the visitor has moved past this prompt (it's no longer the latest message),
            // the buttons are shown greyed-out/inert instead of disappearing — keeps the
            // transcript readable without letting a stale prompt be clicked a second time.
            const isActive = index === messages.length - 1;
            const selectedChoiceId: string | undefined = m.metadata?.selectedChoiceId;
            const handleChoiceClick = (choice: any) => {
              // Mark this message's choice as selected (so it stays visibly highlighted even
              // after later messages push it out of the "active" slot) before sending — the
              // click itself shows no separate chat bubble (hideUserBubble: true).
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === m.id
                    ? { ...msg, metadata: { ...msg.metadata, selectedChoiceId: choice.id } }
                    : msg,
                ),
              );
              sendMessage({ messageText: entryChoiceMessageFor(choice.intent), hideUserBubble: true });
            };
            return (
              <div key={m.id} className="flex justify-start w-full">
                <div className={isActive ? undefined : 'opacity-50 pointer-events-none'}>
                  <div className="flex flex-wrap gap-2">
                    {m.metadata.choices.map((choice: any) => {
                      const isSelected = choice.id === selectedChoiceId;
                      return (
                        <button
                          key={choice.id}
                          type="button"
                          disabled={isSending || !isActive}
                          onClick={() => handleChoiceClick(choice)}
                          className="rounded-full px-4 py-2 text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={
                            isSelected
                              ? {
                                background: theme?.sendBtn || FALLBACK.sendButton,
                                color: '#ffffff',
                                borderColor: theme?.sendBtn || FALLBACK.sendButton,
                              }
                              : {
                                background: theme?.botMsgBg || '#ffffff',
                                color: theme?.sendBtn || FALLBACK.sendButton,
                                borderColor: theme?.sendBtn || FALLBACK.sendButton,
                              }
                          }
                        >
                          {choice.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }

          // Skip the empty assistant placeholder added while a response is still streaming in —
          // the bouncing-dots indicator below already covers that waiting state on its own. A
          // lead-capture-form message has no text content by design (the form IS the content), so
          // it's exempted the same way isAssessment already is.
          if (!isUser && !isAssessment && !isLeadCaptureForm && !messageContent) {
            return null;
          }

          return (
            <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start w-full'}`}>
              <div className={`${isUser ? '' : 'w-full max-w-full overflow-hidden'}`}>
                <div
                  className="w-min-full rounded-xl px-4 py-2.5 text-sm shadow-sm"
                  style={
                    isUser
                      ? {
                        background:
                          theme?.userMsgBg ||
                          FALLBACK.brandGradient,
                        color: theme?.userMsgFont || '#ffffff',
                      }
                      : {
                        background: theme?.botMsgBg || '#ffffff',
                        color: theme?.botMsgFont || '#000000',
                        border: `1px solid ${FALLBACK.border}`,
                      }
                  }
                >
                  <div className="message-content space-y-6">
                    {isAssessment ? (
                      <SymptomCheckerAssessmentMessageBubble
                        metadata={m.metadata}
                      />
                    ) : isLeadCaptureForm ? (
                      <LeadCaptureFormBubble
                        metadata={m.metadata}
                        disabled={isSending}
                        theme={theme}
                        onSubmit={(values) => sendMessage({
                          messageText: '[Lead form submitted]',
                          additionalInputParams: { leadFormSubmission: values },
                          hideUserBubble: true,
                          afterFormSubmission: true,
                        })}
                      />
                    ) : renderChatMarkdown(messageContent)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {isSending && (
          <div className="flex justify-start">
            <div
              className="max-w-[80%] rounded-xl px-4 py-2.5 text-sm shadow-sm"
              style={{
                background: theme?.botMsgBg || '#ffffff',
                color: theme?.botMsgFont || '#000000',
                border: `1px solid ${FALLBACK.border}`,
              }}
            >
              <div className="flex gap-1">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                  ●
                </span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                  ●
                </span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                  ●
                </span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {
        !selectedConversationId &&
        <>
          <div className="p-4 border-t" style={{ borderColor: theme?.inputBorder || FALLBACK.border }}>
            <div
              className="flex items-center gap-2 rounded-xl px-3 py-2 shadow-sm"
              style={{
                background: theme?.inputTextBg || '#ffffff',
                border: `1px solid ${theme?.inputBorder || FALLBACK.border}`,
              }}
            >
              <Button tone="custom"
                className="p-1 transition-opacity hover:opacity-70 flex-shrink-0"
                style={{ color: theme?.botMsgFont || FALLBACK.mutedText }}
                aria-label="Emoji"
              >
                <FaceSmileIcon className="h-5 w-5" />
              </Button>
              <TextArea tone="transparent"
                ref={textareaRef}
                value={input}
                onChange={handleInput}
                rows={1}
                placeholder={theme?.placeholder || 'Type your message...'}
                disabled={isSending}
                className="flex-1 resize-none overflow-auto outline-none focus:border-none focus:outline-none focus:ring-0 placeholder:text-sm"
                style={{
                  maxHeight: '120px',
                  color: theme?.inputTextFont || '#000000',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <Button tone="custom"
                onClick={() => sendMessage()}
                disabled={isSending || !input.trim()}
                className="p-2 rounded-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                style={{
                  background: theme?.sendBtn || FALLBACK.sendButton,
                  color: '#ffffff',
                }}
                aria-label="Send message"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Branding — hidden for specific chatbots on request (currently: Pacific One Health). */}
          {!HIDE_POWERED_BY_CHATBOT_IDS.has(chatbotId ?? '') && (
            <div
              className="px-4 pb-2 text-center text-xs opacity-60"
              style={{ color: theme?.botMsgFont || FALLBACK.mutedText }}
            >
              <PoweredBy />
            </div>
          )}
        </>
      }

    </div>
  );

  if (isLoading) {
    return (
      <div className="h-screen w-full flex bg-c-f3f5fb">
        <div className="w-full md:w-[36%] max-w-xl flex items-center justify-center border-r border-gray-200">
          <div className="text-sm text-gray-600">Loading chat...</div>
        </div>
        <div className="hidden md:flex flex-1 items-center justify-center bg-white">
          <div className="text-sm text-gray-400">Preparing resources…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full flex bg-c-f3f5fb">
        <div className="w-full md:w-[36%] max-w-xl flex items-center justify-center border-r border-gray-200">
          <div className="text-sm text-red-600">{error}</div>
        </div>
        <div className="hidden md:flex flex-1 items-center justify-center bg-white">
          <div className="text-sm text-gray-400">Unable to load resources.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex bg-c-f3f5fb">
      {/* Left column: chat panel */}
      <div className={`w-full ${viewType === "chat-only" ? "md:w-full" : "max-w-xl md:w-[36%]"} flex flex-col border-r border-gray-200`}>
        {chatShell}
      </div>

      {/* Right column: checklist panel */}
      {viewType === "chat-only" ? null : (
        <div className="hidden md:flex flex-1 flex-col m-4 ml-6 rounded-3xl bg-white shadow-[0_18px_45px_rgb(var(--c-0f172a,15_23_42)/0.12)] overflow-hidden">
          {/* Step header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100">
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-400">
                {rightSectionHeading}
              </p>
            </div>
          </div>

          {/* Cards section */}
          <div className="h-[calc(100%-80px)] overflow-y-auto px-8 py-6 space-y-4 bg-gradient-to-b from-white via-white to-violet-50/40">
            {rightSectionData.map((section) => {
              return (
                <>
                  <div className="flex items-center gap-2">
                    {(section.sectionHeading || "").trim().length ? (
                      <p className="text-sm font-semibold text-gray-900">{section.sectionHeading}</p>
                    ) : ""}
                    {(section.sectionCountStats.currentValue || 0) !== 0 ? (
                      <Badge className="inline-flex h-6 items-center rounded-full bg-violet-50 px-2.5 text-xs font-medium text-violet-700">
                        {section.sectionCountStats.currentValue}
                        {(section.sectionCountStats.totalValue || 0) !== 0 ? ` / ${section.sectionCountStats.totalValue}` : ""}
                      </Badge>
                    ) : ""}
                  </div>
                  {section.data.map((item) => {
                    const isActive = activeRightSectionItemId === item.id;
                    return (
                      <Button tone="custom"
                        key={item.id}
                        type="button"
                        onClick={() => setActiveRightSectionItemId(item.id)}
                        className={`w-full rounded-2xl border px-4 py-4 text-left transition-all duration-200 ${isActive
                          ? 'border-violet-400 bg-violet-50/60 shadow-[0_0_0_1px_rgb(var(--c-8b5cf6,139_92_246)/0.35)]'
                          : 'border-gray-200 bg-white hover:border-violet-200 hover:shadow-sm'
                          }`}
                      >
                        <div className="flex items-stretch gap-4">
                          {item.image && (
                            <Button tone="custom"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImage(item.image);
                              }}
                              className="group relative h-20 w-32 overflow-hidden rounded-xl bg-gray-100 shadow-inner transition-transform hover:scale-105 flex-shrink-0"
                            >
                              <img
                                src={item.image}
                                alt={item.title}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = '/default-image.png';
                                }}
                              />
                              {/* Hover overlay with zoom icon */}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                                <div className="opacity-0 transition-opacity group-hover:opacity-100">
                                  <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </Button>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                            {(item.descriptionLines || []).map((description: string) => (
                              <p
                                key={description}
                                className="mt-1 text-xs text-gray-500 leading-relaxed"
                              >
                                {description}
                              </p>
                            ))}
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </>
              )
            })}
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {selectedImage && (
        <Overlay tone="dim-70"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] rounded-lg bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <Button tone="custom"
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-1 transition-transform hover:scale-110 hover:shadow-xl"
              aria-label="Close image viewer"
            >
              <svg
                className="h-6 w-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>

            {/* Image */}
            <img
              src={selectedImage}
              alt="Full view"
              className="max-h-[90vh] w-auto object-contain"
              onError={(e) => {
                e.currentTarget.src = '/default-image.png';
              }}
            />

            {/* Download Button */}
            {/* <a
              href={selectedImage}
              download
              className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700 shadow-lg"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </a> */}
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default PublicChatWidget;
